import React, { useState, useEffect } from 'react';
import { playCorrect, playClap } from '../../utils/sounds';
// For puzzle: playCorrect on solve, playClap on game complete
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import { useAuth } from '../../contexts/AuthContext';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;
const popIn = keyframes`
  0%   { transform: scale(0.8); opacity: 0; }
  70%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Intro Styled Components ──────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR};
  display: flex; flex-direction: column; align-items: center; overflow: hidden;
  font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center; gap: 10px;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatingPiece = styled.div<{ $color: string; $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 48px; height: 48px; border-radius: 12px;
  background: ${p => p.$color};
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; font-weight: 900; color: rgba(255,255,255,0.8);
`;
const CharCard = styled.div`
  width: min(60vw, 220px); aspect-ratio: 1;
  display: flex; align-items: center; justify-content: center;
  font-size: clamp(72px, 20vw, 110px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`
  width: 40px; height: 4px; background: #e0e0e0;
  border-radius: 99px; margin: 0 auto 18px;
`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900;
  margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4);
  font-family: ${FONT}; transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game Styled Components ───────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const MovesBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column; align-items: center;
  padding: 24px 16px; gap: 16px;
`;
const PuzzleGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 6px; padding: 12px; background: #fff;
  border-radius: 20px; box-shadow: 0 4px 24px rgba(0,0,0,0.08);
`;
const Tile = styled.button<{ $empty: boolean }>`
  width: min(26vw, 100px); height: min(26vw, 100px);
  border-radius: 12px; border: none;
  background: ${p => p.$empty ? 'rgba(45,188,175,0.08)' : PRIMARY_GR};
  color: #fff; font-size: clamp(22px, 7vw, 36px); font-weight: 900;
  cursor: ${p => p.$empty ? 'default' : 'pointer'};
  box-shadow: ${p => p.$empty ? 'none' : '0 4px 0 #1a8a80'};
  transition: all 0.15s; font-family: ${FONT};
  animation: ${popIn} 0.2s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 0 #1a8a80; }
  &:active:not(:disabled) { transform: translateY(3px); box-shadow: 0 1px 0 #1a8a80; }
  &:disabled { cursor: default; }
`;
const ActionBtn = styled.button`
  padding: 12px 28px; border-radius: 99px; border: 2px solid #e0e0e0;
  background: #fff; color: #555; font-size: 15px; font-weight: 700;
  cursor: pointer; font-family: ${FONT}; transition: all 0.2s;
  &:hover { border-color: ${PRIMARY}; color: ${PRIMARY}; }
`;
const SolvedBanner = styled.div`
  background: rgba(34,197,94,0.12); color: #16a34a;
  border-radius: 14px; padding: 12px 24px;
  font-size: 16px; font-weight: 700;
  animation: ${fadeInScale} 0.4s ease;
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

// ─── Logic ────────────────────────────────────────────────────────────────────
type Puzzle = number[][];

const INITIAL: Puzzle = [[1,2,3],[4,5,6],[7,8,0]];

function isSolved(p: Puzzle) {
  const flat = p.flat();
  return flat.slice(0, 8).every((n, i) => n === i + 1) && flat[8] === 0;
}

function shuffle(p: Puzzle): Puzzle {
  let flat = p.flat();
  do {
    for (let i = flat.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [flat[i], flat[j]] = [flat[j], flat[i]];
    }
  } while (isSolved([flat.slice(0,3), flat.slice(3,6), flat.slice(6,9)] as Puzzle));
  return [flat.slice(0,3), flat.slice(3,6), flat.slice(6,9)] as Puzzle;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PuzzleGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showIntro, setShowIntro] = useState(true);
  const [puzzle, setPuzzle] = useState<Puzzle>(INITIAL);
  const [moves, setMoves]   = useState(0);
  const [solved, setSolved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const startGame = () => {
    const p = shuffle(INITIAL);
    setPuzzle(p); setMoves(0); setSolved(false); setShowModal(false);
  };

  useEffect(() => { if (!showIntro) startGame(); }, [showIntro]);

  const findEmpty = (): [number, number] | null => {
    for (let r = 0; r < 3; r++)
      for (let c = 0; c < 3; c++)
        if (puzzle[r][c] === 0) return [r, c];
    return null;
  };

  const moveTile = (row: number, col: number) => {
    if (solved) return;
    const empty = findEmpty();
    if (!empty) return;
    const [er, ec] = empty;
    if (!((Math.abs(row - er) === 1 && col === ec) || (Math.abs(col - ec) === 1 && row === er))) return;
    const np = puzzle.map(r => [...r]);
    [np[row][col], np[er][ec]] = [np[er][ec], np[row][col]];
    setPuzzle(np);
    const newMoves = moves + 1;
    setMoves(newMoves);
    if (!user && !authLoading && newMoves > 6 && !showLoginPrompt) {
      setShowLoginPrompt(true);
    }
    if (isSolved(np)) {
      setSolved(true);
      playCorrect();
      setTimeout(() => { setShowModal(true); playClap(); }, 400);
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav>
          <BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
        </TopNav>
        <CharArea>
          <FloatingPiece $color="#ef4444" $top="16%" $left="8%" $delay={0}>1</FloatingPiece>
          <FloatingPiece $color="#3b82f6" $top="12%" $right="10%" $delay={1}>2</FloatingPiece>
          <FloatingPiece $color="#22c55e" $bottom="22%" $left="10%" $delay={2}>3</FloatingPiece>
          <FloatingPiece $color="#f97316" $bottom="18%" $right="8%" $delay={3}>4</FloatingPiece>
          <CharCard>🧩</CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Puzzle <span>Geser!</span></IntroTitle>
          <IntroDesc>Susun angka 1 sampai 8 secara berurutan. Klik kotak yang bersebelahan dengan ruang kosong untuk memindahkannya!</IntroDesc>
          <StartBtn onClick={() => setShowIntro(false)}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Puzzle Geser</GameTitle>
        </NavLeft>
        <MovesBadge>Langkah: {moves}</MovesBadge>
      </GameNav>

      <GameBody>
        <PuzzleGrid>
          {puzzle.map((row, r) =>
            row.map((tile, c) => (
              <Tile
                key={`${r}-${c}`}
                $empty={tile === 0}
                disabled={tile === 0}
                onClick={() => moveTile(r, c)}
              >
                {tile !== 0 ? tile : ''}
              </Tile>
            ))
          )}
        </PuzzleGrid>

        {solved && <SolvedBanner>🎉 Puzzle Selesai!</SolvedBanner>}

        <ActionBtn onClick={startGame}>🔀 Acak Ulang</ActionBtn>
      </GameBody>

      {showModal && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>🏆</ModalEmoji>
            <ModalTitle>Selamat!</ModalTitle>
            <ModalDesc>Kamu selesaikan puzzle dalam <strong>{moves} langkah</strong>!</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate("/games")}>Menu</ModalBtn>
              <ModalBtn $primary onClick={startGame}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Silahkan login terlebih dahulu untuk melanjutkan permainan."
          required
        />
      )}
    </GameWrap>
  );
}
